export class TimeProvider {
  constructor() {
    // Wall-clock baseline when the current segment started (resume/reset)
    this._epochWallMs = this._getWallNow();
    // Virtual timeline base at epoch (ms). now() = base + (wall-epoch)*rate when playing
    this._virtualBaseMs = 0;
    // Playback control
    this._isPaused = false;
    this._playbackRate = 1;
    // Stage context (attached later)
    this.stageContext = null;
  }

  attach(stageContext) {
    this.stageContext = stageContext;

    const api = {
      now: () => this.now(),
      getTime: () => this.getTime(),
      getElapsedTime: () => this.getElapsedTime(),
      pause: () => this.pause(),
      resume: () => this.resume(),
      reset: () => this.reset(),
      seek: (ms) => this.seek(ms),
      setTime: (ms) => this.seek(ms),
      setPlaybackRate: (rate) => this.setPlaybackRate(rate),
      getPlaybackRate: () => this._playbackRate,
      isPaused: () => this._isPaused,
    };

    Object.assign(this.stageContext.api, api);
  }

  // Public time getters
  now() {
    return this.getElapsedTime();
  }

  getTime() {
    return this._getWallNow();
  }

  getElapsedTime() {
    if (this._isPaused) return this._virtualBaseMs;
    const wallNow = this._getWallNow();
    return this._virtualBaseMs + (wallNow - this._epochWallMs) * this._playbackRate;
  }

  getElapsedTimeInSeconds() {
    return this.getElapsedTime() / 1000;
  }

  // Controls
  pause() {
    if (this._isPaused) return;
    // Freeze the virtual timeline
    this._virtualBaseMs = this.getElapsedTime();
    this._isPaused = true;
    this._notify();
  }

  resume() {
    if (!this._isPaused) return;
    this._epochWallMs = this._getWallNow();
    this._isPaused = false;
    this._notify();
  }

  reset() {
    this._epochWallMs = this._getWallNow();
    this._virtualBaseMs = 0;
    this._isPaused = false;
    this._playbackRate = 1;
    this._notify();
  }

  seek(ms) {
    const clamped = isFinite(ms) ? Math.max(0, ms) : 0;
    this._virtualBaseMs = clamped;
    this._epochWallMs = this._getWallNow();
    this._notify();
  }

  setPlaybackRate(rate) {
    const r = Number(rate);
    if (!isFinite(r) || r <= 0) return;
    // Rebase to preserve current visual time when changing rate
    const current = this.getElapsedTime();
    this._playbackRate = r;
    this._epochWallMs = this._getWallNow();
    this._virtualBaseMs = current;
    this._notify();
  }

  // Internals
  _getWallNow() {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  }

  _notify() {
    // Best-effort: request a repaint if available
    try {
      if (
        this.stageContext &&
        this.stageContext.api &&
        typeof this.stageContext.api.markNeedRender === 'function'
      ) {
        this.stageContext.api.markNeedRender();
      } else if (
        this.stageContext &&
        this.stageContext.api &&
        typeof this.stageContext.api.paint === 'function'
      ) {
        this.stageContext.api.paint();
      }
    } catch {}
  }

  dispose() {}
}
